import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import "./style.css";
import { toast } from "react-toastify";
import html2pdf from "html2pdf.js";
import { Capacitor } from "@capacitor/core";
import { getToken } from "../../../utils/tokenService";
import { savePdfBlob } from "../../../utils/nativePdfSave";
import {
  completeDownloadNotification,
  failDownloadNotification,
  startDownloadNotification,
} from "../../../utils/downloadNotification";

const DEFAULT_SCALE = 1;
const MIN_SCALE = 0.5;
const MAX_SCALE = 1.8;


/* ---------- helper for responsive preview ---------- */
const MM_TO_PX = 3.78;           // 1 mm ≈ 3.78 px @ 96 DPI
const A4_WIDTH_MM  = 210;
const A4_HEIGHT_MM = 297;
const calculateScale = () => {
  if (typeof window === "undefined") {
    return DEFAULT_SCALE;
  }

  const a4WidthPx = A4_WIDTH_MM * MM_TO_PX;
  const availableWidth = Math.max(window.innerWidth - 32, 240);

  return Math.min(
    DEFAULT_SCALE,
    availableWidth / a4WidthPx
  );
};

const ShowOfferLetter = ({
  letterLabel = "Offer Letter",
  backToListRoute = "/user_dashboard/offer-letters",
  letterType = "offer",
}) => {
  const [offerLetter, setOfferLetter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [userSignature, setUserSignature] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [signaturePosition, setSignaturePosition] = useState({ x: 90, y: 45 });
  const [isDragging, setIsDragging] = useState(false);
  const [scale, setScale] = useState(DEFAULT_SCALE);
  const [isDownloading, setIsDownloading] = useState(false);
  const pageRef = useRef(null);
  const offerRef = useRef();
  const { id } = useParams();
  const navigate = useNavigate();


  // Fetch user's signature
  useEffect(() => {
    const fetchUserProfile = async () => {
      const token = await getToken();
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/user/profile`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data.signature) {
          setUserSignature(response.data.signature);
        }
        setUserProfile(response.data || null);
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };

    fetchUserProfile();
  }, []);

  useEffect(() => {
    const fetchOfferLetter = async () => {
      const token = await getToken();
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/offer-letter/user/offer-letter/${id}`,
          {
            params: { letterType },
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (response.data) {
          setOfferLetter(response.data);
          setStatus(response.data.status || "sent");
          // If offer is already accepted, set the saved signature position
          if (
            response.data.status === "accepted" &&
            response.data.employeeSignaturePosition
          ) {
            setSignaturePosition(response.data.employeeSignaturePosition);
          }
        } else {
          setError(`Invalid ${letterLabel.toLowerCase()} data format`);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching offer letter:", error);
        if (error.response?.status === 403) {
          setError(`You do not have permission to view this ${letterLabel.toLowerCase()}`);
        } else if (error.response?.status === 404) {
          setError(`${letterLabel} not found`);
        } else {
          setError(`Failed to load ${letterLabel.toLowerCase()}. Please try again later.`);
        }
        setLoading(false);
      }
    };

    if (id) {
      fetchOfferLetter();
    } else {
      setError(`Invalid ${letterLabel.toLowerCase()} ID or authentication token`);
      setLoading(false);
    }
  }, [id, letterLabel, letterType]);

  const handleAccept = async () => {
    try {
      if (!userSignature) {
        toast.error(
          `Please upload your signature in your profile before accepting the ${letterLabel.toLowerCase()}`
        );
        return;
      }
      const token = await getToken();

      // Get the current signature position
      const currentSignaturePosition = {
        x: signaturePosition.x,
        y: signaturePosition.y,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/offer-letter/user/offer-letter/${id}/respond`,
        {
          response: "accept",
          signaturePosition: currentSignaturePosition,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data) {
        setStatus("accepted");
        toast.success(`${letterLabel} accepted successfully!`);
      } else {
        toast.error(response.data.message || `Failed to accept ${letterLabel.toLowerCase()}`);
      }
    } catch (error) {
      console.error("Error accepting offer letter:", error);
      const errorMessage =
        error.response?.data?.message || `Failed to accept ${letterLabel.toLowerCase()}`;
      toast.error(errorMessage);
    }
  };

  const handleReject = async () => {
    const token = await getToken();
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/offer-letter/user/offer-letter/${id}/respond`,
        { response: "reject" },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data) {
        setStatus("rejected");
        toast.success(`${letterLabel} rejected successfully!`);
      } else {
        toast.error(response.data.message || `Failed to reject ${letterLabel.toLowerCase()}`);
      }
    } catch (error) {
      console.error("Error rejecting offer letter:", error);
      const errorMessage =
        error.response?.data?.message || `Failed to reject ${letterLabel.toLowerCase()}`;
      toast.error(errorMessage);
    }
  };

  // Signature dragging handlers
  const handleMouseDown = (e) => {
    if (status !== 'sent') return; // Prevent dragging if already accepted/rejected
    setIsDragging(true);
    e.preventDefault(); // Prevent text selection while dragging
  };

  const handleMouseMove = (e) => {
    if (!isDragging || status !== 'sent') return;
    
    const container = e.currentTarget.getBoundingClientRect();
    const currentScale = scale || 1;
    
    // Calculate position relative to container, accounting for scale
    const x = Math.min(
      Math.max((e.clientX - container.left) / currentScale, 75), // Minimum 75px from left
      container.width / currentScale - 75 // Maximum width-75px from left
    );
    const y = Math.min(
      Math.max((e.clientY - container.top) / currentScale, 30), // Minimum 30px from top
      container.height / currentScale - 30 // Maximum height-30px from top
    );
    
    setSignaturePosition({ x, y });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (userSignature && status === "sent") {
      // Set initial position to center of signature area
      setSignaturePosition({
        x: 220, // Shifted right for better alignment
        y: 60, // Half of container height
      });
    }
  }, [userSignature]);

  useEffect(() => {
    const updateScale = () => {
      setScale(calculateScale());
    };

    updateScale();
    window.addEventListener("resize", updateScale);

    return () => {
      window.removeEventListener("resize", updateScale);
    };
  }, []);

  const ZoomControls = () => (
    <div className="zoom-controls">
      <button
        onClick={() => setScale((prev) => Math.min(MAX_SCALE, prev + 0.1))}
        title="Zoom in"
      >
        +
      </button>
      <button
        onClick={() => setScale((prev) => Math.max(MIN_SCALE, prev - 0.1))}
        title="Zoom out"
      >
        -
      </button>
      <button onClick={() => setScale(calculateScale())} title="Reset zoom">
        Reset
      </button>
    </div>
  );

  /* ---------- PDF download ------------- */
  const downloadPDF = async () => {
    if (isDownloading) return;
    const element = offerRef.current;
    if (!element) {
      toast.error(`${letterLabel} content not found.`);
      return;
    }

    setIsDownloading(true);
    let exportHost = null;
    const notificationId = startDownloadNotification(letterLabel);

    try {
      // Wait for all images (logo, signatures) to finish loading
      const images = element.querySelectorAll("img");
      await Promise.all(
        Array.from(images).map(
          (img) =>
            new Promise((resolve) => {
              if (img.complete) resolve();
              else { img.onload = resolve; img.onerror = resolve; }
            })
        )
      );

      // Clone and normalise — strip viewport zoom so PDF output is always A4 width
      const exportNode = element.cloneNode(true);
      exportNode.classList.add("pdf-export-root");
      exportNode.style.cssText = [
        "transform:none",
        "transform-origin:top left",
        "width:210mm",
        "min-width:210mm",
        "max-width:210mm",
        "height:auto",
        "min-height:297mm",
      ].join(";");

      exportHost = document.createElement("div");
      exportHost.className = "pdf-export-host";
      // Render off-screen so html2canvas can measure it properly
      exportHost.style.cssText =
        "position:fixed;left:-9999px;top:0;background:#fff;z-index:-1;";
      exportHost.appendChild(exportNode);
      document.body.appendChild(exportHost);

      const filename = `${letterLabel.replace(/\s+/g, "")}.pdf`;
      const pdfOptions = {
        margin: [0, 0, 0, 0],
        filename,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          backgroundColor: "#ffffff",
          logging: false,
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: {
          mode: ["css"],
          avoid: [".letter-body p", ".letter-body li", ".signature-section"],
        },
      };

      if (Capacitor.isNativePlatform()) {
        // .toPdf() runs the full html→canvas→jsPDF pipeline first,
        // then .outputPdf("blob") safely extracts the blob.
        const blob = await html2pdf()
          .set(pdfOptions)
          .from(exportNode)
          .toPdf()
          .outputPdf("blob");

        const saved = await savePdfBlob(blob, filename);
          if (!saved?.success) {
          // Last-resort: open the server-side PDF in the native browser
          const token = await getToken();
          const fallbackUrl = `${process.env.REACT_APP_BACKEND_URL}/api/offer-letter/download-offer-letter?offerLetterId=${id}&token=${encodeURIComponent(token)}`;
          try {
            const { Browser } = await import("@capacitor/browser");
            await Browser.open({ url: fallbackUrl });
          } catch {
            window.open(fallbackUrl, "_blank", "noopener,noreferrer");
          }
        }
          completeDownloadNotification(notificationId, letterLabel, { onClick: saved.open });
        } else {
          await html2pdf().set(pdfOptions).from(exportNode).save();
          completeDownloadNotification(notificationId, letterLabel);
        }
    } catch (error) {
      console.error("[downloadPDF] error:", error);
      failDownloadNotification(notificationId, letterLabel);
    } finally {
      if (exportHost?.parentNode) exportHost.parentNode.removeChild(exportHost);
      setIsDownloading(false);
    }
  };

  /* -------------------------------------------------------- */

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>{`Loading ${letterLabel.toLowerCase()}...`}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger">{error}</div>
        <button
          className="btn btn-outline-secondary"
          onClick={() => navigate(backToListRoute)}
        >
          Back to List
        </button>
      </div>
    );
  }

  if (!offerLetter) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger">{`${letterLabel} not found`}</div>
        <button
          className="btn btn-outline-secondary"
          onClick={() => navigate(backToListRoute)}
        >
          Back to List
        </button>
      </div>
    );
  }

  const { letterContent, documentStyle, assets } = offerLetter;
  const { companyInfo, content, signatures } = letterContent;
  const formatCompanyName = (name) =>
    String(name || "")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\s+/g, " ")
      .trim();
  const escapeRegExp = (text) => String(text || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const employeeName =
    offerLetter?.userId?.full_Name || userProfile?.full_Name || "N/A";
  const employeeDesignation =
    offerLetter?.position || userProfile?.designation || "Employee";
  const employeeAddress = [
    userProfile?.house,
    userProfile?.street,
    userProfile?.loc,
    userProfile?.subdist,
    userProfile?.dist,
    userProfile?.state,
    userProfile?.zip,
  ]
    .filter(Boolean)
    .join(", ");

  const splitAddressIntoTwoLines = (addressText) => {
    const safeAddress = String(addressText || "N/A").trim();
    if (safeAddress === "N/A") return ["N/A", ""];

    const parts = safeAddress.split(",").map((p) => p.trim()).filter(Boolean);
    if (parts.length <= 1) return [safeAddress, ""];

    const target = Math.ceil(parts.length / 2);
    const line1 = parts.slice(0, target).join(", ");
    const line2 = parts.slice(target).join(", ");
    return [line1, line2];
  };

  const [addressLine1, addressLine2] = splitAddressIntoTwoLines(employeeAddress);

  // Some legacy offer records were saved with the appointment-letter template.
  // Keep that incorrect text from being shown even if an older API deployment
  // returns it, while leaving real appointment letters untouched.
  const isLegacyAppointmentContent = (value) => {
    const body = String(value || "").replace(/<[^>]*>/g, " ").toLowerCase();
    return /subject\s*:\s*appointment/.test(body) ||
      /appointment\s+for\s+the\s+position/.test(body) ||
      /pleased\s+to\s+inform\s+you\s+that\s+you\s+have\s+been\s+appointed/.test(body) ||
      /your\s+employment\s+will\s+be\s+subject\s+to\s+the\s+following/.test(body);
  };

  const correctedOfferContent = (value) => {
    if (letterType === "appointment" || !isLegacyAppointmentContent(value)) {
      return value;
    }

    const companyName = formatCompanyName(companyInfo?.name) || "our organization";
    const position = employeeDesignation || "Employee";
    return `Date: ${new Date().toLocaleDateString()}\n\nTo,\n${employeeName}\n\nSubject: Offer of Employment - ${position}\n\nDear ${employeeName},\n\nWe are pleased to offer you the position of ${position} with ${companyName}. This offer is subject to completion of our standard joining formalities.\n\nYour role, reporting arrangements, work schedule, compensation, and other employment terms will be shared with you as part of your employment documentation. Please review this offer carefully and contact us if you need any clarification.\n\nTo accept this offer, please sign and return the letter by the requested date. We look forward to welcoming you to ${companyName}.\n\nSincerely,\nFor ${companyName}\nAuthorized Signatory`;
  };

  // Function to safely render HTML content
  const renderHTML = (content) => {
    if (!content) return null;
    const rawCompanyName = String(companyInfo?.name || "").trim();
    const normalizedCompanyName = formatCompanyName(rawCompanyName);
    const cleanedContent = String(content)
      .replace(/(<br\s*\/?>\s*){2,}/gi, "<br />")
      .replace(/<div>\s*<\/div>/gi, "")
      .replace(/<p>\s*<\/p>/gi, "")
      .trim();
    const normalizedContent =
      rawCompanyName && normalizedCompanyName
        ? cleanedContent.replace(
            new RegExp(escapeRegExp(rawCompanyName), "g"),
            normalizedCompanyName
          )
        : cleanedContent;
    return (
      <div
        dangerouslySetInnerHTML={{ __html: normalizedContent.replace(/\n/g, "<br />") }}
      />
    );
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center m-md-3 mb-3">
        <h3>{letterLabel}</h3>
        <div className="d-flex gap-2">
          {status === "sent" && (
            <>
              {!userSignature && (
                <div className="text-danger me-2">
                  * Please upload your signature in profile before accepting
                </div>
              )}
              <button
                className="btn btn-success"
                onClick={handleAccept}
                disabled={!userSignature}
              >
                Accept
              </button>
              <button className="btn btn-danger" onClick={handleReject}>
                Reject
              </button>
            </>
          )}
          {status === "accepted" && (
            <button
              className="btn btn-primary"
              onClick={downloadPDF}
              disabled={isDownloading}
            >
              {isDownloading ? "Generating PDF…" : "Download PDF"}
            </button>
          )}
          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate(backToListRoute)}
          >
            Back to List
          </button>
        </div>
      </div>
      <div className="offer-letter-preview container py-4">
        <div className="page-wrapper">
          <div
            ref={offerRef}
            className="a4-page"
            style={{
              transform: `scale(${scale})`,
              transformOrigin: "top center",
              width: "210mm",
              height: "297mm",
              objectFit: "contain",
            }}
          >
            <div
              className="header offer-letter-header"
              style={{ background: "transparent" }}
            >
              <div className="offer-letter-left-pane">
                {assets?.logo && (
                  <div
                    className="logo-container-user"
                    style={{
                      width: documentStyle?.logoSize?.width || "100px",
                      marginRight: "20px",
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "flex-start",
                      background: "transparent",
                    }}
                  >
                    <img
                      crossOrigin="anonymous"
                      src={`${process.env.REACT_APP_BACKEND_URL}${assets.logo}`}
                      alt="Company Logo"
                      className="logo"
                      style={{
                        width: "100%",
                        background: "transparent",
                      }}
                    />
                  </div>
                )}
              </div>
              <div
                className={`company-details ${assets?.logo ? "has-logo" : "no-logo"}`}
              >
                <h5 className="company-name text-end">{formatCompanyName(companyInfo.name)}</h5>
                <div
                  className="text-end"
                  style={{ marginBottom: "0rem", paddingBottom: "0.2rem" }}
                >
                  {companyInfo.address}
                </div>
                <div
                  className="contact-info text-end"
                  style={{ marginTop: "0rem", paddingTop: "0.2rem" }}
                >
                  Email: {companyInfo.email}
                  {companyInfo.phone && (
                    <>
                      <span className="mx-1">,</span>
                      PH NO-{companyInfo.phone}
                    </>
                  )}
                </div>
              </div>
            </div>

            <hr className="divider" />

            <div className="employee-basic-details employee-basic-details-below-header">
              <p><strong>Name:</strong> {employeeName}</p>
              <p><strong>Designation:</strong> {employeeDesignation}</p>
              <p className="employee-address-two-line">
                <strong>Address:</strong> {addressLine1}
                {addressLine2 ? <><br />{addressLine2}</> : null}
              </p>
            </div>

            <div className="letter-title text-center my-4">
              <h2 className="text-decoration-underline">{letterLabel}</h2>
            </div>

            <div
              className="letter-body"
              style={{
                fontSize: documentStyle?.fontSize || "16px",
                fontFamily: documentStyle?.fontFamily || "Arial, sans-serif",
                whiteSpace: "pre-line",
              }}
            >
              {renderHTML(correctedOfferContent(content))}
            </div>

            <div className="signature-section mt-5">
              <div className="row align-items-end">
                <div className="col-6">
                  <p>Sincerely,</p>
                  <p className="mt-4">FOR {companyInfo.name}</p>
                  {assets?.signature && (
                    <div
                      style={{
                        width: documentStyle?.signatureSize?.width || 150,
                        height: documentStyle?.signatureSize?.height || 80,
                        position: "relative",
                        left: documentStyle?.signaturePosition?.x || 0,
                        top: documentStyle?.signaturePosition?.y || 0,
                      }}
                    >
                      <img
                        crossOrigin="anonymous"
                        src={`${process.env.REACT_APP_BACKEND_URL}/uploads/${assets.signature}`}
                        alt="Authorized Signature"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </div>
                  )}
                  <p className="mt-4">
                    {signatures?.company?.designation || "Authorized Signatory"}
                  </p>
                </div>
                <div className="col-6 text-end">
                  <p className="mt-0">{offerLetter.userId?.full_Name || ""}</p>
                  <div
                    className="signature-area"
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseLeave}
                    style={{
                      position: "relative",
                      height: status === "sent" ? "120px" : "100px",
                      border: status === "sent" ? "1px dashed #dee2e6" : "none",
                      borderRadius: "4px",
                      marginBottom: "0px",
                      cursor: status === "sent" ? "move" : "default",
                    }}
                  >
                    {userSignature && (
                      <img
                        crossOrigin="anonymous"
                        src={`${process.env.REACT_APP_BACKEND_URL}${userSignature}`}
                        alt="Your Signature"
                        style={{
                          width: "150px",
                          position: "absolute",
                          left: `${
                            status === "accepted"
                              ? offerLetter.documentStyle
                                  .employeeSignaturePosition?.x || 0
                              : signaturePosition.x
                          }px`,
                          top: `${
                            status === "accepted"
                              ? offerLetter.documentStyle
                                  .employeeSignaturePosition?.y || 0
                              : signaturePosition.y
                          }px`,
                          cursor: status === "sent" ? "move" : "default",
                          userSelect: "none",
                          pointerEvents: "none",
                          transform: "translate(-50%, -50%)",
                        }}
                        draggable={false}
                      />
                    )}
                  </div>
                  <p className="mt-0">
                    {signatures?.employee?.designation || "Employee Signature"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {status === "sent" && userSignature && (
          <div className="text-muted text-center mt-3">
            * Drag your signature to position it before accepting
          </div>
        )}
        <ZoomControls />
      </div>
    </>
  );
};

export default ShowOfferLetter;
